import type { ReactNode } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Dialog, DialogContent, DialogTitle } from '../ui/custom/Dialog';

type PreviewBadge = {
  label: string;
  color?: string;
  textColor?: string;
};

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  isProcessing?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  previewBadge?: PreviewBadge;
}

export default function DeleteConfirmationModal({
  isOpen,
  title,
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  isProcessing = false,
  onCancel,
  onConfirm,
  previewBadge
}: DeleteConfirmationModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && isOpen && !isProcessing) {
          onCancel();
        }
      }}
    >
      <DialogContent
        className={`${
          isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900'
        } sm:max-w-md`}
      >
        <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
        <div className="space-y-3 pt-1">
          <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>{description}</p>
          {previewBadge && (
            <div className="flex items-center gap-2">
              <span className="text-sm">Preview:</span>
              <span
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: previewBadge.color || (isDark ? '#374151' : '#E5E7EB'),
                  color: previewBadge.textColor || (isDark ? '#F9FAFB' : '#111827')
                }}
              >
                {previewBadge.label}
              </span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className={`px-5 py-2 rounded-lg border transition-colors ${
              isDark
                ? 'border-gray-600 text-white hover:bg-gray-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-100'
            } ${isProcessing ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className={`px-5 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors ${
              isProcessing ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            {isProcessing ? 'Deleting...' : confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
